import React, { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { fmtUSD } from "../../utils/currency";
import CRMPipelineView from "./CRMPipelineView";
import Contact360Drawer from "./Contact360Drawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "new_lead", label: "New Lead", color: "bg-slate-400", border: "border-slate-200" },
  { id: "profile_setup", label: "Profile Setup", color: "bg-blue-400", border: "border-blue-200" },
  { id: "docs_submitted", label: "Docs Submitted", color: "bg-cyan-400", border: "border-cyan-200" },
  { id: "deal_active", label: "Deal Active", color: "bg-amber-400", border: "border-amber-200" },
  { id: "funded", label: "Funded", color: "bg-emerald-500", border: "border-emerald-200" },
  { id: "repeat", label: "Repeat Borrower", color: "bg-green-600", border: "border-green-200" },
];

function derivePipelineStage(b) {
  // Check admin-override first
  const override = b.extra?.pipeline_stage;
  if (override && STAGES.some(s => s.id === override)) return override;
  // Auto-derive
  if ((b.funded_count || 0) > 1) return "repeat";
  if ((b.funded_count || 0) === 1) return "funded";
  if ((b.deal_count || 0) > 0) return "deal_active";
  if ((b.doc_count || 0) > 0) return "docs_submitted";
  if (b.full_name && b.email) return "profile_setup";
  return "new_lead";
}

const TABLE_COLUMNS = [
  { key: "full_name", label: "Name", render: b => (
    <div>
      <div className="text-xs font-semibold text-[#0A0A0A]">{b.full_name || "—"}</div>
      <div className="text-[10px] text-[#888]">{b.email}</div>
    </div>
  )},
  { key: "deal_count", label: "Deals", render: b => <span className="text-xs">{b.deal_count || 0}</span> },
  { key: "funded_count", label: "Funded", render: b => <span className="text-xs text-emerald-600 font-semibold">{b.funded_count || 0}</span> },
  { key: "total_volume", label: "Volume", render: b => <span className="text-xs text-[#b89d5a] font-semibold">{fmtUSD(b.total_volume || 0)}</span> },
  { key: "doc_count", label: "Docs", render: b => <span className="text-xs">{b.doc_count || 0}</span> },
];

export default function CRMBorrowers({ onViewDeal, onEntityNavigate }) {
  const [borrowers, setBorrowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/borrowers`, { headers });
      setBorrowers(res.data.borrowers || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStageChange = (userId, newStage) => {
    setBorrowers(prev => prev.map(b =>
      b.user_id === userId ? { ...b, extra: { ...(b.extra || {}), pipeline_stage: newStage } } : b
    ));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="crm-borrowers-loading">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </div>
  );

  const renderCardContent = (b) => (
    <>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {b.deal_count > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">
            {b.deal_count} deals
          </span>
        )}
        {(b.doc_count || 0) === 0 && b.deal_count > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-100">
            No docs
          </span>
        )}
        {b.funded_count > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-100">
            {b.funded_count} funded
          </span>
        )}
      </div>
      {b.total_volume > 0 && (
        <div className="text-[10px] font-semibold text-[#b89d5a] mt-1">{fmtUSD(b.total_volume)}</div>
      )}
    </>
  );

  return (
    <div data-testid="crm-borrowers">
      <CRMPipelineView
        title="Borrowers"
        subtitle="Pipeline management, document tracking, and borrower success"
        stages={STAGES}
        items={borrowers}
        deriveStageFn={derivePipelineStage}
        renderCardContent={renderCardContent}
        tableColumns={TABLE_COLUMNS}
        onSelectUser={setSelectedUser}
        onStageChange={handleStageChange}
        entityType="borrower"
        testIdPrefix="crm-b"
      />

      <Contact360Drawer open={!!selectedUser} onClose={() => setSelectedUser(null)}
        userId={selectedUser} userType="borrower" onViewDeal={onViewDeal} onEntityNavigate={onEntityNavigate} />
    </div>
  );
}
